import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  ChevronDown,
  ChevronUp,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  PhoneCall,
  Search,
  Users,
  X,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { cn } from "../../lib/utils";
import { useAgents } from "../home/queries";
import { AgentSelect } from "./agent-select";
import { callAnalyticsApi, errorText } from "./api";
import { CallDetail } from "./call-detail";
import {
  DASH,
  formatClock,
  formatDateTime,
  formatNumber,
  formatRate,
  formatSigned,
  humanize,
  levelTone,
  rangeToParams,
  sentimentLabelFor,
  sentimentText,
  sentimentTone,
  topEntries,
} from "./format";
import type { AnalyticsSummary, CallSummary } from "./types";

const PAGE_SIZE = 25;
type FilterChip = "all" | "high_risk" | "low_qa" | "objections" | "positive";

interface AgentMetrics {
  agent_id: string;
  agent_name: string;
  total_calls: number;
  avg_qa: number | null;
  qa_passed_count: number;
  avg_sentiment: number | null;
  avg_cx: number | null;
  high_risk_count: number;
  objection_count: number;
  dominant_emotion: string | null;
  latest_call_at: string | null;
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-line bg-elevated px-3 py-2.5 shadow-xs transition-shadow">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">{label}</div>
      <div className="mt-1 truncate text-lg font-bold text-ink">{value}</div>
      {hint ? <div className="mt-0.5 truncate text-[11px] text-ink-3" title={hint}>{hint}</div> : null}
    </div>
  );
}

function SummaryStrip({ summary }: { summary: AnalyticsSummary }) {
  const dist = summary.sentiment?.distribution ?? {};
  const objections = topEntries(summary.objections, 3);
  const emotions = topEntries(summary.emotions, 3);
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6" aria-label="Summary for the selected range">
      <SummaryCard label="Analysed calls" value={summary.calls.toLocaleString("en-IN")} />
      <SummaryCard label="QA average" value={formatNumber(summary.qa?.avg_score, 1)} hint={summary.qa?.pass_rate != null ? `pass rate ${formatRate(summary.qa.pass_rate)}` : undefined} />
      <SummaryCard
        label="Sentiment average"
        value={formatSigned(summary.sentiment?.avg_score)}
        hint={`${dist.positive ?? 0} pos · ${dist.neutral ?? 0} neu · ${dist.negative ?? 0} neg`}
      />
      <SummaryCard label="CX average" value={formatNumber(summary.cx?.avg_score, 1)} hint={summary.cx?.high_risk_calls != null ? `${summary.cx.high_risk_calls} high risk` : undefined} />
      <SummaryCard
        label="Top objections"
        value={objections.length ? humanize(objections[0][0]) : DASH}
        hint={objections.map(([k, n]) => `${humanize(k)} ${n}`).join(" · ") || undefined}
      />
      <SummaryCard
        label="Top emotions"
        value={emotions.length ? humanize(emotions[0][0]) : DASH}
        hint={emotions.map(([k, n]) => `${humanize(k)} ${n} calls`).join(" · ") || undefined}
      />
    </div>
  );
}

function AgentCard({ agent, onSelect }: { agent: AgentMetrics; onSelect: () => void }) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        "group relative flex flex-col justify-between rounded-2xl border border-line bg-elevated p-4 sm:p-5 transition-all duration-200 cursor-pointer shadow-xs",
        "hover:border-brand/50 hover:shadow-md hover:-translate-y-0.5",
        agent.high_risk_count > 0 && "border-l-4 border-l-rose-500",
      )}
    >
      <div>
        {/* Top Header of Card */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand font-semibold text-sm group-hover:bg-brand group-hover:text-white transition-colors">
              <Bot className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-ink group-hover:text-brand transition-colors">
                {agent.agent_name}
              </h3>
              <p className="font-mono text-[11px] text-ink-3 truncate">
                {agent.agent_id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="rounded-full bg-soft px-2.5 py-1 text-xs font-semibold text-ink">
              {agent.total_calls} {agent.total_calls === 1 ? "call" : "calls"}
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-soft text-ink-3 group-hover:bg-brand/10 group-hover:text-brand transition-colors">
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>

        {/* Metrics Grid inside Card */}
        {agent.total_calls > 0 ? (
          <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-canvas/60 p-2.5 border border-line/50">
            {/* QA */}
            <div className="text-center">
              <div className="text-[10px] uppercase font-semibold text-ink-3">QA Avg</div>
              <div className="text-sm font-bold text-ink mt-0.5">
                {agent.avg_qa != null ? formatNumber(agent.avg_qa, 1) : "—"}
              </div>
            </div>

            {/* Sentiment */}
            <div className="text-center border-x border-line/60">
              <div className="text-[10px] uppercase font-semibold text-ink-3">Sentiment</div>
              <div className={cn(
                "text-sm font-bold mt-0.5",
                agent.avg_sentiment != null && agent.avg_sentiment > 0 ? "text-emerald-600 dark:text-emerald-400" :
                agent.avg_sentiment != null && agent.avg_sentiment < 0 ? "text-rose-600 dark:text-rose-400" :
                "text-ink",
              )}>
                {agent.avg_sentiment != null ? formatSigned(agent.avg_sentiment) : "—"}
              </div>
            </div>

            {/* CX */}
            <div className="text-center">
              <div className="text-[10px] uppercase font-semibold text-ink-3">CX Score</div>
              <div className="text-sm font-bold text-ink mt-0.5">
                {agent.avg_cx != null ? formatNumber(agent.avg_cx, 1) : "—"}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 py-3 text-center text-xs text-ink-3 rounded-xl bg-canvas/40 border border-dashed border-line">
            No analysed calls in selected date range
          </div>
        )}
      </div>

      {/* Footer tags */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-line/60 pt-3 text-xs text-ink-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {agent.high_risk_count > 0 && (
            <Badge tone="danger">
              {agent.high_risk_count} high risk
            </Badge>
          )}
          {agent.objection_count > 0 && (
            <Badge tone="warning">
              {agent.objection_count} objection{agent.objection_count === 1 ? "" : "s"}
            </Badge>
          )}
          {agent.dominant_emotion && (
            <Badge tone="violet">
              {humanize(agent.dominant_emotion)}
            </Badge>
          )}
        </div>

        {agent.latest_call_at && (
          <span className="text-[11px] font-medium text-ink-3">
            Last: {formatDateTime(agent.latest_call_at)}
          </span>
        )}
      </div>
    </div>
  );
}

function CallRow({ call, selected, onSelect }: { call: CallSummary; selected: boolean; onSelect: () => void }) {
  const h = call.headline;
  const sentiment = h.sentiment_label ?? sentimentLabelFor(h.sentiment_score);
  const isHighRisk = h.escalation_risk === "high";
  const isMediumRisk = h.escalation_risk === "medium";

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "w-full space-y-1.5 px-3.5 py-2.5 text-left transition-colors border-l-4",
          selected
            ? "border-l-brand bg-brand/10 dark:bg-brand/15"
            : isHighRisk
            ? "border-l-rose-500 hover:bg-soft"
            : isMediumRisk
            ? "border-l-amber-500 hover:bg-soft"
            : "border-l-transparent hover:bg-soft",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-ink">
            {call.agent_name || call.agent_id || "Unknown agent"}
          </span>
          <span className="shrink-0 font-mono text-xs tabular-nums text-ink-3">
            {formatClock(call.duration_s)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-ink-3">
          <span>{formatDateTime(call.call_at)}</span>
          <span className="font-mono text-[11px] opacity-70" title={call.call_id}>
            #{call.call_id.slice(-8)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          {h.qa_score != null ? (
            <Badge tone={h.qa_passed === true ? "success" : h.qa_passed === false ? "danger" : "neutral"}>
              QA {formatNumber(h.qa_score)}
            </Badge>
          ) : null}
          {sentiment ? (
            <Badge tone={sentimentTone(sentiment)}>
              {sentimentText(h.sentiment_score, sentiment)}
            </Badge>
          ) : null}
          {h.cx_score != null ? <Badge tone="neutral">CX {formatNumber(h.cx_score)}</Badge> : null}
          {h.escalation_risk ? (
            <Badge tone={levelTone(h.escalation_risk)}>
              {humanize(h.escalation_risk)} risk
            </Badge>
          ) : null}
          {h.objection_count ? (
            <Badge tone="warning">
              {h.objection_count} obj{h.objection_count === 1 ? "" : "s"}
            </Badge>
          ) : null}
          {h.dominant_emotion ? (
            <Badge tone="violet">
              {humanize(h.dominant_emotion)}
            </Badge>
          ) : null}
        </div>
      </button>
    </li>
  );
}

function CallsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_7"), preset: "last_7" }));
  const agentId = searchParams.get("agent") || "";
  const selectedCall = searchParams.get("call");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeChip, setActiveChip] = useState<FilterChip>("all");
  const [isFeedCollapsed, setIsFeedCollapsed] = useState(false);

  const registeredAgents = useAgents();

  const handleSelectAgent = useCallback((nextAgentId: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextAgentId) {
      next.set("agent", nextAgentId);
      next.delete("call");
    } else {
      next.delete("agent");
      next.delete("call");
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectCall = useCallback((callId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("call", callId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Summary query: overall when agentId is empty, or filtered to agent
  const summaryParams = useMemo(
    () => ({ ...rangeToParams(range.start, range.end), agent_id: agentId || null }),
    [range, agentId],
  );
  const summary = useQuery({
    queryKey: ["call-analytics", "summary", summaryParams],
    queryFn: () => callAnalyticsApi.summary(summaryParams),
  });

  // Query for calls
  const callsParams = useMemo(
    () => ({ ...rangeToParams(range.start, range.end), agent_id: agentId || null }),
    [range, agentId],
  );
  const calls = useInfiniteQuery({
    queryKey: ["call-analytics", "calls", callsParams],
    queryFn: ({ pageParam }) => callAnalyticsApi.listCalls({ ...callsParams, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });
  const items = useMemo(
    () => calls.data?.pages.flatMap((page) => page.items) ?? [],
    // `calls` is the full query object; `calls.data` is the specific slice we track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calls.data],
  );

  // Per-agent summary queries to get accurate call counts (not limited by pagination)
  const agentIds = useMemo(
    () => (registeredAgents.data ?? []).map((a) => a.agent_id),
    [registeredAgents.data],
  );
  const agentSummaries = useQuery({
    queryKey: ["call-analytics", "agent-summaries", summaryParams, agentIds],
    queryFn: async () => {
      if (agentIds.length === 0) return {};
      const results = await Promise.all(
        agentIds.map((aid) =>
          callAnalyticsApi.summary({ ...summaryParams, agent_id: aid }).then((s) => [aid, s] as const),
        ),
      );
      return Object.fromEntries(results) as Record<string, import("./types").AnalyticsSummary>;
    },
    enabled: agentIds.length > 0,
  });

  // Aggregated Agent Cards for the Overview view
  const agentCards = useMemo(() => {
    const map = new Map<string, { agent_id: string; agent_name: string; calls: CallSummary[] }>();

    (registeredAgents.data ?? []).forEach((a) => {
      map.set(a.agent_id, {
        agent_id: a.agent_id,
        agent_name: a.agent_name || a.name || a.agent_id,
        calls: [],
      });
    });

    items.forEach((c) => {
      const aid = c.agent_id || "unknown";
      const existing = map.get(aid);
      if (existing) {
        existing.calls.push(c);
        if (!existing.agent_name && c.agent_name) existing.agent_name = c.agent_name;
      } else {
        map.set(aid, {
          agent_id: aid,
          agent_name: c.agent_name || aid,
          calls: [c],
        });
      }
    });

    const result: AgentMetrics[] = [];
    map.forEach((data, id) => {
      const cList = data.calls;

      // Use the per-agent summary for an accurate total count; fall back to
      // locally-loaded items only if the summary hasn't arrived yet.
      const agentSummaryData = agentSummaries.data?.[id];
      const total = agentSummaryData != null ? agentSummaryData.calls : cList.length;

      let sumQa = 0;
      let countQa = 0;
      let qaPassed = 0;
      let sumSentiment = 0;
      let countSentiment = 0;
      let sumCx = 0;
      let countCx = 0;
      let highRisk = 0;
      let totalObjections = 0;
      const emotionsCount: Record<string, number> = {};
      let latestTime: string | null = null;

      cList.forEach((c) => {
        const h = c.headline;
        if (h.qa_score != null) {
          sumQa += h.qa_score;
          countQa++;
          if (h.qa_passed === true) qaPassed++;
        }
        if (h.sentiment_score != null) {
          sumSentiment += h.sentiment_score;
          countSentiment++;
        }
        if (h.cx_score != null) {
          sumCx += h.cx_score;
          countCx++;
        }
        if (h.escalation_risk === "high") {
          highRisk++;
        }
        if (h.objection_count) {
          totalObjections += h.objection_count;
        }
        if (h.dominant_emotion) {
          emotionsCount[h.dominant_emotion] = (emotionsCount[h.dominant_emotion] || 0) + 1;
        }
        if (!latestTime || (c.call_at && new Date(c.call_at) > new Date(latestTime))) {
          latestTime = c.call_at;
        }
      });

      const dominantEmotion = Object.entries(emotionsCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

      result.push({
        agent_id: id,
        agent_name: data.agent_name,
        total_calls: total,
        avg_qa: countQa ? sumQa / countQa : null,
        qa_passed_count: qaPassed,
        avg_sentiment: countSentiment ? sumSentiment / countSentiment : null,
        avg_cx: countCx ? sumCx / countCx : null,
        high_risk_count: highRisk,
        objection_count: totalObjections,
        dominant_emotion: dominantEmotion,
        latest_call_at: latestTime,
      });
    });

    return result.sort((a, b) => {
      if (b.total_calls !== a.total_calls) return b.total_calls - a.total_calls;
      return a.agent_name.localeCompare(b.agent_name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredAgents.data, items, agentSummaries.data]);

  // Client-side quick filter and search over loaded calls in agent detail view
  const filteredItems = useMemo(() => {
    return items.filter((call) => {
      const h = call.headline;
      if (activeChip === "high_risk" && h.escalation_risk !== "high") return false;
      if (activeChip === "low_qa" && h.qa_passed !== false && !(h.qa_score != null && h.qa_score < 70)) return false;
      if (activeChip === "objections" && (h.objection_count ?? 0) <= 0) return false;
      if (activeChip === "positive" && h.sentiment_label !== "positive" && !(h.sentiment_score != null && h.sentiment_score > 0.3)) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchAgent = (call.agent_name || "").toLowerCase().includes(q) || (call.agent_id || "").toLowerCase().includes(q);
        const matchId = (call.call_id || "").toLowerCase().includes(q);
        if (!matchAgent && !matchId) return false;
      }
      return true;
    });
  }, [items, activeChip, searchQuery]);

  // Auto-select first call of the agent when in agent view
  useEffect(() => {
    if (agentId && !selectedCall && filteredItems.length > 0) {
      selectCall(filteredItems[0].call_id);
    }
  }, [agentId, selectedCall, filteredItems, selectCall]);

  const selectedAgentObj = agentCards.find((a) => a.agent_id === agentId);
  const selectedAgentName = selectedAgentObj?.agent_name || agentId;

  // -------------------------------------------------------------
  // VIEW 1: All Agents Overview (KPIs top + Agent Cards bottom)
  // -------------------------------------------------------------
  if (!agentId) {
    return (
      <div className="flex flex-1 min-h-0 flex-col space-y-4">
        {/* Top Control Bar */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/60 pb-2.5">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-semibold text-ink">Call Intelligence</h1>
            <span className="rounded-full bg-soft px-2.5 py-0.5 text-xs text-ink-3 font-medium">
              Overview
            </span>
          </div>

          <div className="flex items-center gap-2">
            <DateRangePicker value={range} onChange={setRange} align="right" />
            <AgentSelect value={agentId} onChange={handleSelectAgent} includeAll />
          </div>
        </div>

        {/* Top Overall KPIs (as requested in sketch) */}
        {summary.isLoading ? (
          <div className="h-20 animate-pulse rounded-2xl bg-soft" />
        ) : summary.isError ? (
          <p role="alert" className="rounded-xl border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
            {errorText(summary.error, "Summary could not be loaded.")}
          </p>
        ) : summary.data ? (
          <div className="shrink-0">
            <SummaryStrip summary={summary.data} />
          </div>
        ) : null}

        {/* Agent Cards Section (as requested in sketch) */}
        <div className="flex flex-1 min-h-0 flex-col space-y-3">
          <div className="flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-ink-3" />
              <h2 className="text-sm font-semibold text-ink">Agents ({agentCards.length})</h2>
            </div>
            <span className="text-xs text-ink-3">Click an agent to inspect calls & analytics</span>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            {registeredAgents.isLoading && calls.isLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-brand" />
              </div>
            ) : agentCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-ink-3 border border-dashed border-line rounded-2xl">
                <Bot className="h-8 w-8 opacity-40 mb-2" />
                <p className="text-sm font-medium text-ink">No agents found</p>
                <p className="text-xs text-ink-3 mt-1">Create an agent or adjust the date filter to see intelligence data.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agentCards.map((agent) => (
                  <AgentCard
                    key={agent.agent_id}
                    agent={agent}
                    onSelect={() => handleSelectAgent(agent.agent_id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW 2: Agent Drilldown (Calls feed + Call Inspector Workbench)
  // -------------------------------------------------------------
  return (
    <div className="flex flex-1 min-h-0 flex-col space-y-3">
      {/* Breadcrumb Header row */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/60 pb-2.5">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => handleSelectAgent("")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-elevated px-2.5 py-1 text-xs font-medium text-ink hover:bg-soft transition-colors shadow-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All Agents
          </button>
          <span className="text-ink-3">/</span>
          <span className="text-sm font-semibold text-ink flex items-center gap-1.5">
            <Bot className="h-4 w-4 text-brand" />
            {selectedAgentName}
          </span>
          <span className="rounded-full bg-soft px-2 py-0.5 text-xs text-ink-3 font-medium">
            {items.length} {items.length === 1 ? "call" : "calls"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <DateRangePicker value={range} onChange={setRange} align="right" />
          <AgentSelect value={agentId} onChange={handleSelectAgent} includeAll />
        </div>
      </div>

      {/* Main Split Workbench: Left Feed + Right Inspector */}
      <div className="flex flex-1 min-h-0 gap-3.5 relative overflow-hidden">
        {/* Left Column: Call Feed */}
        <section
          aria-label="Analysed calls"
          className={cn(
            "flex flex-col min-h-0 h-full rounded-2xl border border-line bg-elevated shadow-xs transition-all duration-300 ease-in-out shrink-0 overflow-hidden",
            isFeedCollapsed
              ? "w-0 opacity-0 -ml-3.5 border-0 p-0 pointer-events-none"
              : "w-full lg:w-[22rem] opacity-100",
          )}
        >
          {/* Feed Header: Search & Quick Filters */}
          <div className="shrink-0 space-y-2 border-b border-line p-3 bg-canvas/40">
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" aria-hidden />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search call ID..."
                  className="w-full rounded-lg border border-line bg-canvas pl-8 pr-7 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 focus:ring-brand"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFeedCollapsed(true)}
                title="Collapse call list"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-3 hover:bg-soft hover:text-ink transition-colors shrink-0"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>

            {/* Quick Filter Chips */}
            <div className="flex items-center gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {([
                { id: "all", label: "All" },
                { id: "high_risk", label: "High Risk" },
                { id: "low_qa", label: "Low QA" },
                { id: "objections", label: "Objections" },
                { id: "positive", label: "Positive" },
              ] as const).map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveChip(id)}
                  className={cn(
                    "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors shrink-0",
                    activeChip === id
                      ? "bg-brand text-white shadow-xs"
                      : "bg-soft text-ink-3 hover:bg-line hover:text-ink",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Call Feed Items */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {calls.isLoading ? (
              <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading calls">
                <Loader2 className="animate-spin text-brand" />
              </div>
            ) : calls.isError ? (
              <p role="alert" className="p-4 text-sm text-danger">{errorText(calls.error, "Calls could not be loaded.")}</p>
            ) : filteredItems.length === 0 ? (
              <div className="p-6 text-center text-xs text-ink-3">
                {searchQuery || activeChip !== "all"
                  ? "No calls match current search/filter."
                  : "No analysed calls for this agent in this range."}
              </div>
            ) : (
              <>
                <ul className="divide-y divide-line">
                  {filteredItems.map((call) => (
                    <CallRow
                      key={call.call_id}
                      call={call}
                      selected={call.call_id === selectedCall}
                      onSelect={() => selectCall(call.call_id)}
                    />
                  ))}
                </ul>
                {calls.hasNextPage ? (
                  <button
                    type="button"
                    onClick={() => calls.fetchNextPage()}
                    disabled={calls.isFetchingNextPage}
                    className="w-full border-t border-line px-3 py-2 text-xs font-medium text-brand hover:bg-soft disabled:opacity-50 transition-colors"
                  >
                    {calls.isFetchingNextPage ? "Loading more calls…" : "Load more calls"}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </section>

        {/* Right Column: Call Inspector (Detail View) */}
        <section
          aria-label="Call analysis"
          onClick={() => {
            if (!isFeedCollapsed) {
              setIsFeedCollapsed(true);
            }
          }}
          className="flex flex-1 min-w-0 flex-col min-h-0 h-full rounded-2xl border border-line bg-elevated overflow-hidden shadow-xs transition-all duration-300 ease-in-out"
        >
          {selectedCall ? (
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
              <CallDetail
                key={selectedCall}
                callId={selectedCall}
                isFeedCollapsed={isFeedCollapsed}
                onExpandFeed={() => setIsFeedCollapsed(false)}
                feedCallsCount={filteredItems.length}
              />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-ink-3">
              {isFeedCollapsed && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFeedCollapsed(false);
                  }}
                  className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-brand/40 bg-brand/10 px-3 py-1.5 text-xs font-semibold text-brand hover:bg-brand/20 transition-colors shadow-xs"
                >
                  <PanelLeftOpen className="h-4 w-4" /> Show calls ({filteredItems.length})
                </button>
              )}
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-soft text-ink-3 mb-3">
                <PhoneCall className="h-6 w-6 opacity-60" />
              </span>
              <p className="text-sm font-medium text-ink">No call selected</p>
              <p className="mt-1 text-xs text-ink-3 max-w-xs">
                Select a call from the feed to inspect speech tone, emotion, QA compliance, objections, and conversation transcript.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function CallAnalyticsPage() {
  const config = useQuery({ queryKey: ["call-analytics", "config"], queryFn: callAnalyticsApi.config });

  return (
    <div className="flex flex-1 min-h-0 flex-col space-y-3 px-4 sm:px-5 md:px-6 pb-4 pt-3 h-full">
      {config.data && !config.data.master_enabled ? (
        <p role="status" className="shrink-0 rounded-xl border border-line bg-soft px-3 py-2 text-xs text-ink-2">
          Call Analytics is not enabled for your organization, so no new calls are analysed. TechLadder turns it on.
        </p>
      ) : null}

      <CallsTab />
    </div>
  );
}

export { PromptInsightsPage } from "./prompt-insights-page";
